// Libraries
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Store
import { RootState } from '../../app/store';

// API
import { postMessage } from './chatApi';

// Models
import { Message } from './chatModels';

/**
 * @interface ChatState
 * Describes the properties for the global chat state
 *
 * @property {array} messages List of chat messages
 * @property {array} notifiedPlayers List of online players already seen
 * @property {array} messagesOpen Whether the chat history is displayed
 * @property {array} emojiListOpen Whether the emoji list menu is displayed
 * @property {string} status Current data fetching status
 */
export interface ChatState {
    messages: Message[];
    notifiedPlayers: string[];
    messagesOpen: boolean;
    emojiListOpen: boolean;
    status: 'idle' | 'loading' | 'failed';
}

/**
 * @const initialState
 * Describes the initial chat state on application start up
 */
const initialState: ChatState = {
    messages: [],
    notifiedPlayers: [],
    messagesOpen: false,
    emojiListOpen: false,
    status: 'idle',
};

/**
 * @function postMessageAsync
 * Posts chat message to API
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} userId The current user's ID to authenticate
 * @param {number} sessionId The current session ID
 * @param {string} messageBody The message body
 *
 * @return {object} Object containing submitted message data
 */
export const postMessageAsync = createAsyncThunk(
    'session/pushMessage',
    async ({
        sessionId,
        messageBody,
    }: {
        sessionId: number;
        messageBody: string;
    }) => {
        const response = await postMessage(sessionId, messageBody);

        return response.data;
    },
);

/**
 * @const chatSlice
 * Describes initial state and reducer functions to handle chat state changes
 * https://redux-toolkit.js.org/api/createslice
 */
export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        pushMessage: (state, action) => {
            state.messages = [...state.messages, action.payload];
        },
        pushNotifiedPlayers: (state, action) => {
            state.notifiedPlayers = [...state.notifiedPlayers, action.payload];
        },
        pushMessagesStatus: (state, action: PayloadAction<boolean>) => {
            state.messagesOpen = action.payload;
        },
        pushEmojiListStatus: (state, action: PayloadAction<boolean>) => {
            state.emojiListOpen = action.payload;
        },
        clearChatHistory: (state) => {
            state.messages = initialState.messages;
            state.notifiedPlayers = initialState.notifiedPlayers;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(postMessageAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(postMessageAsync.fulfilled, (state) => {
                state.status = 'idle';
            })
            .addCase(postMessageAsync.rejected, (state) => {
                state.status = 'failed';
            });
    },
});

/**
 * @const selectMessages
 * Exposes messages property in chat state for use in rest of application
 */
export const selectMessages = (state: RootState) => state.chat.messages;

/**
 * @const selectNotifiedPlayers
 * Exposes notifiedPlayers property in chat state for use in rest of application
 */
export const selectNotifiedPlayers = (state: RootState) =>
    state.chat.notifiedPlayers;

/**
 * @const selectMessagesStatus
 * Exposes messagesOpen property in chat state for use in rest of application
 */
export const selectMessagesStatus = (state: RootState) =>
    state.chat.messagesOpen;

/**
 * @const selectEmojiListStatus
 * Exposes emojiListOpen property in chat state for use in rest of application
 */
export const selectEmojiListStatus = (state: RootState) =>
    state.chat.emojiListOpen;

/**
 * @const pushMessage
 * Exposes pushMessage action in chat state for use in rest of application
 */
export const { pushMessage } = chatSlice.actions;

/**
 * @const pushNotifiedPlayers
 * Exposes pushNotifiedPlayers action in chat state for use in rest of application
 */
export const { pushNotifiedPlayers } = chatSlice.actions;

/**
 * @const pushMessagesStatus
 * Exposes pushMessagesStatus action for use in rest of application
 */
export const { pushMessagesStatus } = chatSlice.actions;

/**
 * @const pushEmojiListStatus
 * Exposes pushEmojiListStatus action for use in rest of application
 */
export const { pushEmojiListStatus } = chatSlice.actions;

/**
 * @const clearChatHistory
 * Exposes clearChatHistory action in chat state for use in rest of application
 */
export const { clearChatHistory } = chatSlice.actions;

/**
 * @const chatSlice.reducer
 * Exposes chat reducer for use in store
 */
export default chatSlice.reducer;
